import { z } from 'zod';
import { baseEntitySchema, idSchema, instantSchema } from './common.js';
import { customValuesSchema } from './fields.js';
import { pipelinePlaceSchema } from './projects.js';
/**
 * The candidate record (RELAY-939) as the API will serve it: the union of what Relay stores
 * in `recruiting.resumes` and `recruitcrm.candidates`, under the same names. Every field is
 * nullable because the source data is sparse (about 40% of RecruitCRM's standard fields are
 * under 5% filled); the database table is `candidates` in @crm/db.
 *
 * `stage`, `status`, `work_type` and `gender` are open text on purpose: their vocabularies
 * are RecruitCRM's hand-typed ones today and get normalised in a later ticket, not lost here.
 */
export const GENDERS = ['male', 'female', 'other'];
export const candidateSchema = baseEntitySchema.extend({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /**
     * Served as stored, not re-validated: a bulk load (the history import) can hold an address the
     * write check refuses (`name@domain.com.`), and a strict read schema turned every response
     * carrying one into a 500 — the record's page and every list page with it on (DATA-1306, 117
     * candidates on staging). Writes still check it (`candidateInputSchema`).
     */
    email: z.string().nullable(),
    phone: z.string().nullable(),
    altPhone: z.string().nullable(),
    /** Generated by the database from `email` (lower-cased, trimmed); unique per organisation. */
    primaryEmail: z.string().nullable(),
    /** Generated by the database from `phone` (digits only); unique per organisation. */
    primaryPhone: z.string().nullable(),
    dateOfBirth: z.iso.date().nullable(),
    gender: z.string().nullable(),
    currentRole: z.string().nullable(),
    currentCompany: z.string().nullable(),
    yearsExperience: z.number().int().nullable(),
    educationLevel: z.string().nullable(),
    workType: z.string().nullable(),
    region: z.string().nullable(),
    country: z.string().nullable(),
    languages: z.array(z.string()).nullable(),
    /** USD per hour, two decimals, as a string (Postgres numeric). */
    expectedHourlyRate: z.string().nullable(),
    stage: z.string().nullable(),
    status: z.string().nullable(),
    source: z.string().nullable(),
    ownerId: idSchema.nullable(),
    applicationDate: instantSchema.nullable(),
    interviewDate: instantSchema.nullable(),
    onboardingDate: instantSchema.nullable(),
    isBlocked: z.boolean(),
    crmDisqualified: z.boolean(),
    resumeFileLink: z.string().nullable(),
    resumeFilename: z.string().nullable(),
    photoUrl: z.string().nullable(),
    portfolioUrl: z.string().nullable(),
    voiceRecordingUrl: z.string().nullable(),
    recruitCrmId: z.string().nullable(),
    recruitCrmSlug: z.string().nullable(),
    relayResumeId: z.string().nullable(),
    /** The current resume as a CRM document (RELAY-942); `resumeFileLink` is the legacy Relay URL. */
    resumeDocumentId: idSchema.nullable(),
    /** Read-only: the document `resumeDocumentId` points at, resolved by the API. */
    resumeDocument: z
        .object({
        id: idSchema,
        filename: z.string(),
        contentType: z.string(),
        sizeBytes: z.number().int().nonnegative(),
    })
        .nullable(),
    /** Custom field values by definition key (RELAY-941); validated against the organisation's definitions. */
    custom: customValuesSchema.default({}),
    /** Small unstructured bag; prefer a custom field. */
    extra: z.record(z.string(), z.unknown()).nullable().default(null),
});
/**
 * One candidate as its page reads it: the record plus every live Candidate Flow pipeline it is
 * in, the default first, then in the order it joined (archived places included, with
 * `archivedAt`). Lists and searches serve `candidateSchema` without it.
 */
export const candidateDetailSchema = candidateSchema.extend({
    pipelines: z.array(pipelinePlaceSchema),
});
/**
 * What a client may send to create or update a candidate: everything the database does not derive.
 *
 * `stage` is the stage NAME in the default Candidate Flow pipeline (`candidates.stage` mirrors
 * it): on a create or a change it must name a stage of that pipeline (400 otherwise), and the
 * candidate is placed or moved there. `stageId` names a stage of ANY live Candidate Flow
 * pipeline: on a create it must be the default's; on an update it moves the candidate within
 * that stage's pipeline, which it must already be in (409). A create with neither starts at the
 * default's first open stage.
 */
export const candidateInputSchema = candidateSchema
    .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    primaryEmail: true,
    primaryPhone: true,
    resumeDocument: true,
})
    .partial()
    .extend({
    isBlocked: z.boolean().optional(),
    crmDisqualified: z.boolean().optional(),
    // What a person types is still checked as an address, though reads serve what is stored.
    email: z.email().nullable().optional(),
    // No defaults on input: an absent key must stay absent (a PATCH touches only what it carries).
    custom: customValuesSchema.optional(),
    extra: z.record(z.string(), z.unknown()).nullable().optional(),
    stageId: idSchema.optional(),
});
/**
 * The candidate fields a filter may name, with the field TYPE that decides the operators and
 * value shape (`validateFilter`). Open vocabularies (stage, status, work type…) are
 * single_select so `in` works on them; `languages` is a multi_select (array overlap).
 */
export const CANDIDATE_FIELDS = {
    id: 'reference',
    firstName: 'text',
    lastName: 'text',
    email: 'text',
    phone: 'text',
    altPhone: 'text',
    primaryEmail: 'text',
    primaryPhone: 'text',
    dateOfBirth: 'date',
    gender: 'single_select',
    currentRole: 'text',
    currentCompany: 'text',
    yearsExperience: 'number',
    educationLevel: 'text',
    workType: 'single_select',
    region: 'text',
    country: 'text',
    languages: 'multi_select',
    expectedHourlyRate: 'number',
    stage: 'single_select',
    status: 'single_select',
    source: 'single_select',
    ownerId: 'user',
    applicationDate: 'datetime',
    interviewDate: 'datetime',
    onboardingDate: 'datetime',
    isBlocked: 'boolean',
    crmDisqualified: 'boolean',
    resumeFilename: 'text',
    recruitCrmId: 'text',
    recruitCrmSlug: 'text',
    relayResumeId: 'text',
    createdAt: 'datetime',
    updatedAt: 'datetime',
};
export const CANDIDATE_FIELD_NAMES = Object.keys(CANDIDATE_FIELDS);
/** Fields a list may be ordered by. Keyset paging needs a total order, so `id` is always the tiebreaker. */
export const CANDIDATE_SORTABLE = [
    'createdAt',
    'updatedAt',
    'firstName',
    'lastName',
    'email',
    'stage',
    'status',
    'country',
    'applicationDate',
    'interviewDate',
    'expectedHourlyRate',
    'yearsExperience',
];
//# sourceMappingURL=candidates.js.map